import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Filter,
  RefreshCcw,
  Search,
  SlidersHorizontal,
  Star,
} from "lucide-react";

import ReviewCard from "../../components/dashboard/production/ReviewCard";
import PaginationFooter from "../../components/dashboard/production/PaginationFooter";

import { useBusiness } from "../../context/BusinessContext";
import { getBusinessReviews } from "../../lib/businessService";

const tabs = [
  { key: "all", label: "All Reviews" },
  { key: "needs_response", label: "Needs Response" },
  { key: "pending_approval", label: "Pending Approval" },
  { key: "responded", label: "Responded" },
  { key: "archived", label: "Archived" },
];

export default function Reviews() {
  const { selectedBusiness } = useBusiness();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [sortMode, setSortMode] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    if (!selectedBusiness?.id) return;

    async function loadReviews() {
      setLoading(true);
      setError("");

      try {
        const data = await getBusinessReviews(selectedBusiness.id);
        setReviews(data || []);
      } catch (err) {
        console.error(err);
        setError("Unable to load reviews.");
      } finally {
        setLoading(false);
      }
    }

    loadReviews();
  }, [selectedBusiness?.id]);

  const stats = useMemo(() => {
    const total = reviews.length;

    const needsResponse = reviews.filter(
      (r) =>
        !r.responseText &&
        [
          "pending_response",
          "draft_generated",
          "new",
          "needs_approval",
        ].includes(r.status),
    ).length;

    const responded = reviews.filter(
      (r) => r.responseText || r.status === "posted",
    ).length;

    const lowRating = reviews.filter((r) => Number(r.rating) <= 3).length;

    return {
      total,
      needsResponse,
      responded,
      lowRating,
    };
  }, [reviews]);

  const filteredReviews = useMemo(() => {
    const value = search.trim().toLowerCase();

    let filtered = [...reviews];

    if (value) {
      filtered = filtered.filter((review) => {
        return (
          review.authorName?.toLowerCase().includes(value) ||
          review.content?.toLowerCase().includes(value) ||
          review.status?.toLowerCase().includes(value) ||
          String(review.rating).includes(value)
        );
      });
    }

    switch (activeTab) {
      case "needs_response":
        filtered = filtered.filter(
          (r) =>
            !r.responseText &&
            ["pending_response", "draft_generated", "new"].includes(r.status),
        );
        break;

      case "pending_approval":
        filtered = filtered.filter((r) =>
          ["needs_approval", "draft_generated"].includes(r.status),
        );
        break;

      case "responded":
        filtered = filtered.filter(
          (r) => r.responseText || r.status === "posted",
        );
        break;

      case "archived":
        filtered = filtered.filter((r) => r.status === "archived");
        break;

      default:
        break;
    }

    if (ratingFilter !== "all") {
      filtered = filtered.filter(
        (r) => Number(r.rating) === Number(ratingFilter),
      );
    }

    if (sortMode === "newest") {
      filtered.sort((a, b) => new Date(b.reviewDate) - new Date(a.reviewDate));
    }

    if (sortMode === "oldest") {
      filtered.sort((a, b) => new Date(a.reviewDate) - new Date(b.reviewDate));
    }

    if (sortMode === "highest_rating") {
      filtered.sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0));
    }

    if (sortMode === "lowest_rating") {
      filtered.sort((a, b) => Number(a.rating || 0) - Number(b.rating || 0));
    }

    return filtered;
  }, [reviews, search, activeTab, ratingFilter, sortMode]);

  useEffect(() => {
    setPage(1);
  }, [search, activeTab, ratingFilter, sortMode]);

  const paginatedReviews = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    return filteredReviews.slice(start, end);
  }, [filteredReviews, page]);

  if (!selectedBusiness?.id) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
        <h2 className="text-xl font-semibold text-slate-900">
          No business selected
        </h2>

        <p className="text-slate-500 mt-2">
          Select or add a business to view reviews.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Reviews
          </h1>

          <p className="text-slate-500 mt-1">
            Browse, search, and monitor customer feedback for{" "}
            <span className="font-medium text-slate-700">
              {selectedBusiness.name}
            </span>
            .
          </p>
        </div>

        <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2">
          <RefreshCcw size={16} />
          Sync Reviews
        </button>
      </header>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <ReviewStatCard
          title="Total Reviews"
          value={loading ? "..." : stats.total}
          subtitle="Synced from Google"
          icon={<Star size={22} className="text-indigo-600" />}
          iconBg="bg-indigo-100"
        />

        <ReviewStatCard
          title="Needs Response"
          value={loading ? "..." : stats.needsResponse}
          subtitle="Reviews awaiting action"
          icon={<AlertCircle size={22} className="text-orange-600" />}
          iconBg="bg-orange-100"
        />

        <ReviewStatCard
          title="Responded"
          value={loading ? "..." : stats.responded}
          subtitle="Reviews with owner replies"
          icon={<CheckCircle size={22} className="text-green-600" />}
          iconBg="bg-green-100"
        />

        <ReviewStatCard
          title="Low Rating"
          value={loading ? "..." : stats.lowRating}
          subtitle="3 stars or below"
          icon={<AlertCircle size={22} className="text-red-600" />}
          iconBg="bg-red-100"
        />
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 space-y-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const active = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                  active
                    ? "bg-indigo-600 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by reviewer, rating, status, or keyword..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Filter size={17} className="text-slate-400" />

              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="highest_rating">Highest rating</option>
                <option value="lowest_rating">Lowest rating</option>
              </select>
            </div>

            <button
              onClick={() => setShowFilters((current) => !current)}
              className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 transition flex items-center justify-center gap-2 text-sm font-medium"
            >
              <SlidersHorizontal size={16} />
              Filters
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  Rating
                </label>

                <select
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
                >
                  <option value="all">All ratings</option>
                  <option value="5">5 stars</option>
                  <option value="4">4 stars</option>
                  <option value="3">3 stars</option>
                  <option value="2">2 stars</option>
                  <option value="1">1 star</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
          <p className="text-sm text-slate-600">
            Showing{" "}
            <span className="font-semibold text-slate-900">
              {filteredReviews.length}
            </span>{" "}
            review{filteredReviews.length === 1 ? "" : "s"}
          </p>

          {(search || ratingFilter !== "all" || activeTab !== "all") && (
            <button
              onClick={() => {
                setSearch("");
                setRatingFilter("all");
                setActiveTab("all");
              }}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
            >
              Clear filters
            </button>
          )}
        </div>
      </section>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <p className="text-slate-500">Loading reviews...</p>
        </div>
      ) : filteredReviews.length > 0 ? (
        <>
          <div className="space-y-4">
            {paginatedReviews.map((review) => (
              <ReviewCard
                key={review.id}
                name={review.authorName}
                date={formatDate(review.reviewDate)}
                rating={review.rating}
                review={review.content}
                responseText={review.responseText}
                suggestedResponse={review.suggestedResponse}
                status={mapStatus(review.status)}
              />
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <PaginationFooter
              page={page}
              pageSize={pageSize}
              totalItems={filteredReviews.length}
              onPageChange={setPage}
            />
          </div>
        </>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
          <h2 className="text-lg font-semibold text-slate-900">
            No reviews found
          </h2>

          <p className="text-slate-500 mt-2">
            Try adjusting your search or filters.
          </p>
        </div>
      )}
    </div>
  );
}

function ReviewStatCard({ title, value, subtitle, icon, iconBg }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <h3 className="text-3xl font-bold text-slate-900 mt-2">{value}</h3>
          <p className="text-sm text-slate-500 mt-2">{subtitle}</p>
        </div>

        <div
          className={`h-11 w-11 rounded-2xl flex items-center justify-center ${iconBg}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function formatDate(date) {
  if (!date) return "Unknown date";

  return new Date(date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function mapStatus(status) {
  if (!status) return "needs_response";

  if (
    status === "pending_response" ||
    status === "draft_generated" ||
    status === "needs_approval" ||
    status === "new"
  ) {
    return "needs_response";
  }

  return status;
}
